import { HttpClient, HttpParams } from "@angular/common/http";
import { Inject, Injectable } from "@angular/core";
import { Observable } from "rxjs";

import {
	EnvironmentConfig,
	ENV_CONFIG
} from "@core/environments/environment-config";
import { HttpParamsConfig } from "@shared/schemas/http-params-config";

@Injectable({
	providedIn: "root"
})
export class BaseCrudService {
	API_URL = "";

	constructor(
		protected http: HttpClient,
		@Inject(ENV_CONFIG) private config: EnvironmentConfig
	) {
		this.API_URL = `${config.environment.API}`;
	}

	getAll<T>(
		path: string,
		httpParamsConfig?: HttpParamsConfig,
		fullPath = false
	): Observable<T> {
		let httpParams = new HttpParams();

		if (httpParamsConfig) {
			httpParams = httpParams
				.set("customFilter", true)
				.set("page", httpParamsConfig.page?.toString() ?? "")
				.set("size", httpParamsConfig.size?.toString() ?? "")
				.set("sort", httpParamsConfig.sort ?? "");

			httpParamsConfig.fields?.forEach((field) => {
				httpParams = httpParams.set(field.key, field.value);
			});
		}
		return this.http.get<T>(fullPath ? path : `${this.API_URL}/${path}`, {
			params: httpParams
		});
	}

	getById<T>(path: string, id: number): Observable<T> {
		return this.http.get<T>(`${this.API_URL}/${path}/${id}`);
	}

	getAutoComplete<T>(path: string, searchText: string): Observable<T> {
		return this.http.get<T>(
			`${this.API_URL}/${path}?searchText=${searchText}`
		);
	}

	save<T extends { id?: string | number }>(
		path: string,
		object: T
	): Observable<T> {
		if (object.id) {
			return this.update(path, object);
		} else {
			return this.create(path, object);
		}
	}

	private update<T extends { id?: string | number }>(
		path: string,
		object: T
	) {
		return this.http.put<T>(
			`${this.API_URL}/${path}/${Number(object.id)}`,
			object
		);
	}

	private create<T>(path: string, object: T) {
		return this.http.post<T>(`${this.API_URL}/${path}`, object);
	}

	delete(path: string, id: number): Observable<unknown> {
		return this.http.delete<unknown>(`${this.API_URL}/${path}/${id}`);
	}

	saveWithPathId<T>(
		path: string,
		id: number,
		object: unknown
	): Observable<T> {
		return this.http.post<T>(
			`${this.config.environment.API}/${path}/${id}`,
			object
		);
	}

	deleteWithBody<T>(
		path: string,
		id: number,
		object?: unknown
	): Observable<T> {
		return this.http.delete<T>(`${this.API_URL}/${path}/${id}`, {
			body: object
		});
	}
}
